import { camelCasifyProperties } from 'src/services/Api.utils'

// Public channel events
export const PRODUCT_DELETED = 'react-product-deleted'
export const PRODUCT_UPDATED = 'react-product-updated'
export const REACT_STOCK_BULK_UPDATE_ARRIVALS = 'react-stock-bulk-update-arrivals'
export const REACT_STOCK_BULK_UPDATE_NEW_DAY = 'react-stock-bulk-update-new-day'
export const REACT_STOCK_UPDATED_BUYER = 'react-stock-updated-buyer'
export const REACT_FIXED_SHIPPING_DATE_QUANTITY_UPDATED =
  'react-fixed-shipping-date-quantity-updated'

// Private channel events
export const CART_ITEMS_CANCELLED = 'react-items-cancelled'
export const CART_ITEMS_TIMEOUT = 'react-items-timeout'
export const ORDER_CANCELLED_BY_BUYER = 'react-order-cancelled'
export const ORDER_CREATED = 'react-order-created'
export const ORDER_ITEM_CANCELLED_BY_BUYER = 'react-order-item-cancelled'
export const ORDER_ITEM_UPDATED = 'react-order-item-updated'
export const ORDER_UPDATED = 'react-order-updated'
export const PUSHER_USER_REDIRECT = 'react-user-redirect'
export const REACT_BUYER_COMPANY_UPDATED = 'react-buyer-company-updated'
export const REACT_MARKETPLACE_UPDATED = 'react-ecosystem-updated'
export const REACT_SELLER_UPDATED = 'react-seller-updated'
export const REACT_STOCK_UPDATED_SELLER = 'react-stock-updated-seller'
export const SELLER_PRODUCT_INSTANCE_DELETED = 'react-seller-product-instance-deleted'
export const SELLER_PRODUCT_INSTANCE_UPDATED = 'react-seller-product-instance-updated'
export const REACT_ORDERED_QUANTITIES_UPDATED = 'react-ordered-quantities-updated'

export interface PusherChannel {
  bind: (event: string, callback: Function) => void
  unbind: (event: string, callback: Function) => void
}

declare global {
  interface Window {
    pusher?: {
      channel: (channelName: string) => PusherChannel | undefined
      subscribe: (channelName: string) => PusherChannel | undefined
    }
    pusherPublicChannel?: PusherChannel
  }
}

export interface PusherBindingConfiguration {
  [key: string]: Function
}
export interface PusherChannelCallbacks {
  [key: string]: Function
}
export enum PusherPrivateChannelType {
  BUYER = 'buyer',
  MARKETPLACE = 'ecosystem',
  ORGANISATION = 'organisation',
  SELLER = 'seller',
}
export type PusherUnsubscribeFunction = () => void

export type PusherSubscribeFunction = (
  bindingConfigutation: PusherBindingConfiguration
) => PusherUnsubscribeFunction

export type PusherPrivateChannelSubscribeFunction = (
  bindingConfigutation: PusherBindingConfiguration
) => PusherUnsubscribeFunction

export type PusherPrivateChannel = (
  uuid: string,
  type: PusherPrivateChannelType
) => {
  instance?: PusherChannel
  subscribe: PusherPrivateChannelSubscribeFunction
}
export interface PusherPublicChannel {
  instance: Window['pusherPublicChannel']
  subscribe: PusherSubscribeFunction
}

export interface Pusher {
  instance: Window['pusher']
  publicChannel: PusherPublicChannel
  privateChannel: PusherPrivateChannel
}

export const camelCasifyPusherPayload = (bindingCallback: Function) => (payload: object) =>
  bindingCallback(camelCasifyProperties(payload))

/* Retrieve pusher from window (given by Django template in index.html) and publicChannel */
const getPusher = (): Pusher => {
  const publicChannelCallbacks: PusherChannelCallbacks = {}
  const privateChannelCallbacks: PusherChannelCallbacks = {}

  return {
    instance: window.pusher,
    publicChannel: {
      instance: window.pusherPublicChannel,
      subscribe: (bindingConfiguration: PusherBindingConfiguration) => {
        Object.keys(bindingConfiguration).forEach(event => {
          publicChannelCallbacks[event] = camelCasifyPusherPayload(bindingConfiguration[event])
          if (window.pusherPublicChannel)
            window.pusherPublicChannel.bind(event, publicChannelCallbacks[event])
        })
        /* unsubscribing function */
        return () => {
          Object.keys(bindingConfiguration).forEach(event => {
            if (window.pusherPublicChannel)
              window.pusherPublicChannel.unbind(event, publicChannelCallbacks[event])
            delete publicChannelCallbacks[event]
          })
        }
      },
    },
    privateChannel: (uuid: string, type: PusherPrivateChannelType) => {
      const pusherPrivateChannelName = `${type}-${uuid}`
      const pusherPrivateChannel = window.pusher
        ? window.pusher.channel(pusherPrivateChannelName) ||
          window.pusher.subscribe(pusherPrivateChannelName)
        : undefined

      return {
        instance: pusherPrivateChannel,
        subscribe: (bindingConfiguration: PusherBindingConfiguration) => {
          Object.keys(bindingConfiguration).forEach(event => {
            privateChannelCallbacks[event] = camelCasifyPusherPayload(bindingConfiguration[event])
            if (pusherPrivateChannel)
              pusherPrivateChannel.bind(event, privateChannelCallbacks[event])
          })
          /* unsubscribing function */
          return () => {
            Object.keys(bindingConfiguration).forEach(event => {
              if (pusherPrivateChannel) {
                pusherPrivateChannel.unbind(event, privateChannelCallbacks[event])
                delete privateChannelCallbacks[event]
              }
            })
          }
        },
      }
    },
  }
}

export default getPusher
